import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDashboard } from '../../../../features/dashboard/dashboardThunks';
import { setFilters } from '../../../../features/dashboard/dashboardSlice';
import { COMPANY_COLORS, DASHBOARD_MIN_REFETCH_MS } from '../../../../features/dashboard/dashboardConstants';
import { fetchDeliveryFilterOptions } from '../../../../features/deliveries/deliveriesThunks';
import { setFilters as setDeliveryFilters } from '../../../../features/deliveries/deliveriesSlice';
import { setFilters as setAttentionFilters } from '../../../../features/attention/attentionSlice';
import { fetchAdminOptions } from '../../../../features/admins/adminsThunks';
import { fetchSellerOptions } from '../../../../features/sellers/sellersThunks';
import { selectCompanyOptionsByAdmin } from '../../../../features/sellers/sellersSlice';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';

import { DashboardFilters } from './components/DashboardFilters';
import { DashboardKpis } from './components/DashboardKpis';
import { DashboardCharts } from './components/DashboardCharts';
import { RecentActivityCard } from './components/RecentActivityCard';
import { NeedsAttentionCard } from './components/NeedsAttentionCard';
import { RankingCard } from './components/RankingCard';
import { FAMILY_COLOR } from '../../../../utils/chartColors';

export const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    kpis, previous, range, statusCount, activity, daily, communeCount, companyCount, needsAttention,
    filters, isLoading, isStale, lastFetchedAt, error,
  } = useSelector((state) => state.dashboard);
  const { companyOptions } = useSelector((state) => state.deliveries);
  const { isConnected } = useSelector((state) => state.notifications);
  // These numbers are the whole platform's, so they also have to be readable
  // one operator at a time.
  const adminOptions = useSelector((state) => state.admins.options);
  // Everything → one operator → one of its companies. See the deliveries table.
  const adminFilter = filters.admin_id ?? 'all';
  const adminCompanyOptions = useSelector(selectCompanyOptionsByAdmin(adminFilter));

  useEffect(() => {
    dispatch(fetchDashboard());
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(fetchAdminOptions());
    // The sellers catalog is what says which company belongs to which operator.
    dispatch(fetchSellerOptions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDeliveryFilterOptions());
  }, [dispatch]);

  // Reconnecting is the moment we know we may have missed notices: the backend
  // publishes them and forgets, so anything that moved while the socket was
  // down never arrives.
  const wasConnected = useRef(isConnected);
  useEffect(() => {
    if (isConnected && !wasConnected.current) dispatch(fetchDashboard());
    wasConnected.current = isConnected;
  }, [isConnected, dispatch]);

  // A status change on the backend marks the numbers stale. Refetching straight
  // away would follow every burst, so the automatic path waits out
  // `DASHBOARD_MIN_REFETCH_MS`; until then the filter bar says there are
  // changes and the button is right there.
  useEffect(() => {
    if (!isStale || isLoading) return;

    const elapsed = Date.now() - (lastFetchedAt || 0);
    if (elapsed >= DASHBOARD_MIN_REFETCH_MS) {
      dispatch(fetchDashboard());
      return;
    }

    const timer = setTimeout(() => dispatch(fetchDashboard()), DASHBOARD_MIN_REFETCH_MS - elapsed);
    return () => clearTimeout(timer);
  }, [isStale, isLoading, lastFetchedAt, dispatch]);

  const handleReload = useCallback(() => dispatch(fetchDashboard()), [dispatch]);

  /**
   * A metric on the attention card opens the queue filtered to its reason.
   *
   * The reason travels in the URL so the link is shareable and survives a
   * reload; the range and company go through the store, the same way the status
   * chips hand their filter to the deliveries table.
   */
  const handleAttentionClick = useCallback(
    (reason) => {
      dispatch(
        setAttentionFilters({
          reason,
          start_date: filters.start_date,
          end_date: filters.end_date,
          company: filters.company,
        })
      );
      navigate(`/deliveries/attention?reason=${reason}`);
    },
    [dispatch, navigate, filters]
  );

  const handleFilterChange = useCallback(
    // Changing operator drops the company: one that is not theirs would leave
    // every number at zero, which reads as an outage rather than as a filter.
    (patch) => dispatch(setFilters('admin_id' in patch ? { ...patch, company: 'all' } : patch)),
    [dispatch]
  );

  /**
   * A chip leaves for the deliveries table carrying the dashboard's own filter.
   *
   * The status stays out of this screen on purpose — filtering the dashboard by
   * one status leaves a single slice at 100% — so the click hands the question
   * to the screen that can actually answer it.
   */
  const handleStatusClick = useCallback(
    (status) => {
      dispatch(
        setDeliveryFilters({
          delivery_status: status,
          start_date: filters.start_date,
          end_date: filters.end_date,
          company: filters.company,
          // The operator travels too: a chip clicked while looking at one must
          // not open the table with the whole platform in it.
          admin_id: filters.admin_id ?? 'all',
          page: 1,
        })
      );
      navigate('/deliveries');
    },
    [dispatch, navigate, filters]
  );

  const handleActivityClick = useCallback(
    (orderId) => navigate(`/order-details/${orderId}`),
    [navigate]
  );

  const filterBar = (
    <DashboardFilters
      filters={filters}
      onFilterChange={handleFilterChange}
      companyOptions={companyOptions}
      adminOptions={adminOptions}
      adminCompanyOptions={adminFilter === 'all' ? null : adminCompanyOptions}
      onReload={handleReload}
      isLoading={isLoading}
      isStale={isStale}
    />
  );

  // Nothing to show yet: one notice takes the place of the cards, which would
  // otherwise read as a grid of convincing zeros.
  if (error && !kpis) {
    return (
      <div className="space-y-6">
        {filterBar}
        <div
          data-testid="dashboard-error"
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border"
        >
          <ErrorState title="No se pudo cargar la información" description={error} onRetry={handleReload} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {filterBar}

      {/* The numbers from before stay; the strip says they may be stale. */}
      {error && <StaleDataNotice testId="dashboard-stale-error" error={error} onRetry={handleReload} />}

      <DashboardKpis kpis={kpis} previous={previous} isLoading={isLoading} />

      {/* Full width and above the charts: it is the only card here you can act
          on, and it used to be the narrow column beside the one you cannot. */}
      <NeedsAttentionCard
        onMetricClick={handleAttentionClick}
        kpis={kpis}
        items={needsAttention}
        onItemClick={handleActivityClick}
        isLoading={isLoading}
      />

      {/* No `items-start` here on purpose: both cards stretch to the row, so
          the charts card is exactly as tall as the activity feed and stops
          resizing when you switch tabs. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DashboardCharts
          daily={daily}
          range={range}
          statusCount={statusCount}
          total={kpis?.total || 0}
          onStatusClick={handleStatusClick}
          isLoading={isLoading}
        />

        <RecentActivityCard
          activity={activity}
          onEntryClick={handleActivityClick}
          isLoading={isLoading}
        />
      </div>

      {/* `items-start` so a short card does not stretch to match a tall one:
          eight communes next to two companies would leave the second card mostly
          empty. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <RankingCard
          title="Dónde estás despachando"
          subtitle="Las comunas con más envíos del periodo"
          data={communeCount}
          colorFor={() => FAMILY_COLOR.delivered}
          emptyMessage="Sin comunas en el periodo seleccionado"
          isLoading={isLoading}
        />

        {/* Always rendered, even with nothing to draw. A card that comes and
            goes reshuffles the whole row, and an empty state says "no data"
            far more clearly than a gap where a card used to be. */}
        <RankingCard
          title="Por empresa"
          subtitle="Reparto de envíos entre tus clientes"
          data={companyCount}
          colorFor={(_, index) => COMPANY_COLORS[index % COMPANY_COLORS.length]}
          emptyMessage="Sin envíos por empresa en el periodo"
          isLoading={isLoading}
        />

      </div>
    </div>
  );
};
